import isEmpty from 'lodash/isEmpty'
import some from 'lodash/some'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

export interface AdyenV2ConfigProps {
  clientKey: string
  environment: string
  paymentMethodsResponse: {
    paymentMethods: Array<{ configuration?: any, issuers?: Array<{ id: string, name: string }>, type: string }>
  }
}

interface AdyenContext2Props {
  adyenConfig?: AdyenV2ConfigProps | null
  adyenInstance: any | null
}

export const AdyenContext2 = createContext<AdyenContext2Props>({
  adyenInstance: null,
  adyenConfig: undefined,
})

const { publicRuntimeConfig } = getConfig()

const isScriptLoaded = (scriptUrl) => {
  const scripts = document.getElementsByTagName('script')
  return some(scripts, script => script.src.startsWith(scriptUrl))
}
interface AdyenContextProviderV2Props {
  adyenConfig?: AdyenV2ConfigProps | null
  children: React.JSX.Element
}
export const AdyenContextProviderV2: FC<AdyenContextProviderV2Props> = ({ adyenConfig, children }) => {
  const [adyenInstance, setAdyenInstance] = useState<unknown>(null)
  const { store } = useContext(MetadataContext)
  const { locale = '' } = useRouter()
  const { t } = useTranslation()

  const initialiseAdyen = useCallback(async () => {
    if (!adyenConfig) {
      setAdyenInstance(null)
      return
    }
    const adyenLocale = locale.replace('-', '_')
    // @ts-ignore
    const newAdyenInstance = await window.AdyenCheckout({
      ...adyenConfig,
      locale: adyenLocale,
      translations: {
        [adyenLocale]: {
          'creditCard.numberField.title': t('checkout:CARD_NUMBER', 'Card Number'),
          'creditCard.numberField.invalid': t('checkout:COF_CARD_NUMBER_IS_INVALID', 'Woops! The entered card number is invalid'),
          'creditCard.expiryDateField.title': t('checkout:EXPIRY_DATE', 'Expiry date'),
          'creditCard.expiryDateField.placeholder': t('checkout:MM_YY', 'MM/YY'),
          'creditCard.expiryDateField.invalid': t('checkout:EXPIRY_DATE_IS_INVALID', 'Woops! The expiry date is invalid'),
          'creditCard.expiryDateField.month': t('checkout:COF_MONTH', 'Month'),
          'creditCard.expiryDateField.year': t('checkout:COF_YEAR', 'Year'),
          'creditCard.oneClickVerification.invalidInput.title': t('checkout:INVALID_CVC_CVV_FORMAT', 'Invalid CVC / CVV format'),
        },
      },
      analytics: {
        enabled: false,
      },
      countryCode: store.config?.countryCode,
      styles: {
        base: {
          caretColor: '#000',
        },
        placeholder: {
          color: 'var(--neutral)',
        },
      },
    })
    setAdyenInstance(newAdyenInstance)
  }, [adyenConfig, locale, store.config?.countryCode, t])

  useEffect(() => {
    if (!adyenConfig) {
      return
    }
    const scriptLoaded = isScriptLoaded('https://checkoutshopper')
    const head: HTMLHeadElement | null = document.querySelector('head')
    if (!head || scriptLoaded) {
      return
    }
    const script = document.createElement('script')
    const link = document.createElement('link')
    const adyenUrl = `https://checkoutshopper-${publicRuntimeConfig.adyenEnv}.adyen.com/checkoutshopper/sdk/5.63.0`
    script.setAttribute('src', `${adyenUrl}/adyen.js`)
    script.setAttribute('integrity', 'sha384-wEt/Yz/g97VSgMpNDVCPTyr8FYuIpeOgh42UNr346/yK3z2yfaIixeuLWXG6q0XU')
    script.setAttribute('crossorigin', 'anonymous')
    link.setAttribute('href', `${adyenUrl}/adyen.css`)
    link.setAttribute('rel', 'stylesheet')
    link.setAttribute('integrity', 'sha384-Dk62669n9Ic7V6K8X7MBAOEZ5IQ9Qq29nW/zPkfwg1ghqyZLiuSc5QYQJ6M72iNR')
    link.setAttribute('crossorigin', 'anonymous')
    head.appendChild(script)
    head.appendChild(link)
    script.onload = () => initialiseAdyen()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo(
    () => ({ adyenInstance, adyenConfig }),
    [adyenInstance, adyenConfig],
  )

  if (isEmpty(adyenConfig)) {
    return children
  }
  return (
    <AdyenContext2.Provider value={value}>
      {children}
    </AdyenContext2.Provider>
  )
}
